import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { eventAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart3,
  Users,
  CheckCircle,
  Mail,
  FileText,
  Download,
  Loader2,
  Calendar
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';

export default function Reports() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchReport();
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      const response = await eventAPI.list();
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    setLoadingReport(true);
    try {
      const response = await eventAPI.getReport(selectedEvent);
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoadingReport(false);
    }
  };

  const feedbackChartData = report?.feedback_summary ? [
    { name: 'Objectives Met', score: report.feedback_summary.avg_objectives_met },
    { name: 'Evidence Based', score: report.feedback_summary.avg_evidence_based },
    { name: 'Relevance', score: report.feedback_summary.avg_relevance },
    { name: 'Clinical Change', score: report.feedback_summary.avg_clinical_change },
  ] : [];

  const downloadAttendanceSheet = () => {
    if (!report) return;
    
    const headers = ['Name', 'Email', 'Provider Number', 'Checked In', 'Arrival Time', 'Departure Time', 'Certificate Sent'];
    const rows = report.attendees.map(a => [
      a.name,
      a.email,
      a.provider_number || '',
      a.checked_in ? 'Yes' : 'No',
      a.arrival_time ? format(parseISO(a.arrival_time), 'h:mm a') : '',
      a.departure_time ? format(parseISO(a.departure_time), 'h:mm a') : '',
      a.certificate_sent ? 'Yes' : 'No'
    ]);
    
    const csvContent = [
      `Event: ${report.event.title}`,
      `Date: ${report.event.date}`,
      `Location: ${report.event.location}`,
      `Duration: ${report.event.duration_hours} hours`,
      '',
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `attendance_${report.event.date}_${report.event.title.replace(/\s+/g, '_')}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success('Attendance sheet downloaded');
  };

  return (
    <Layout activeNav="reports">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Reports</h1>
          <p className="text-muted-foreground">View attendance reports and feedback summaries</p>
        </div>

        {/* Event Selector */}
        <Card className="mb-6 border-border">
          <CardHeader>
            <CardTitle className="text-lg">Select Event</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedEvent} onValueChange={setSelectedEvent}>
              <SelectTrigger className="h-11" data-testid="report-event-selector">
                <SelectValue placeholder="Choose an event to view report" />
              </SelectTrigger>
              <SelectContent>
                {events.map((event) => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.title} ({event.date})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {loadingReport ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : report ? (
          <>
            {/* Event Summary */}
            <Card className="mb-6 border-border">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{report.event.title}</CardTitle>
                    <CardDescription className="mt-1">
                      {format(parseISO(report.event.date), 'MMMM d, yyyy')} • {report.event.location}
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    className="gap-2"
                    onClick={downloadAttendanceSheet}
                    data-testid="download-attendance-btn"
                  >
                    <Download className="w-4 h-4" />
                    Download Attendance
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-5 h-5 text-sky-500" />
                    <span className="text-sm text-muted-foreground">Registered</span>
                  </div>
                  <p className="text-3xl font-bold">{report.stats.total_registered}</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <span className="text-sm text-muted-foreground">Checked In</span>
                  </div>
                  <p className="text-3xl font-bold">{report.stats.checked_in}</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Mail className="w-5 h-5 text-violet-500" />
                    <span className="text-sm text-muted-foreground">Certs Sent</span>
                  </div>
                  <p className="text-3xl font-bold">{report.stats.certificates_sent}</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <FileText className="w-5 h-5 text-amber-500" />
                    <span className="text-sm text-muted-foreground">Feedback</span>
                  </div>
                  <p className="text-3xl font-bold">{report.feedback_summary.total_responses}</p>
                </CardContent>
              </Card>
            </div>

            {/* Feedback Chart */}
            {report.feedback_summary.total_responses > 0 && (
              <Card className="mb-6 border-border">
                <CardHeader>
                  <CardTitle className="text-lg">Feedback Summary</CardTitle>
                  <CardDescription>
                    Average scores from {report.feedback_summary.total_responses} responses (out of 5)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={feedbackChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 12 }} 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          domain={[0, 5]} 
                          tick={{ fontSize: 12 }}
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar 
                          dataKey="score" 
                          fill="hsl(var(--primary))" 
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Attendees Table */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg">Attendance Sheet</CardTitle>
                <CardDescription>
                  Complete list of registered attendees
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="attendance-table">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Name</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Email</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Provider #</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Arrival</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Departure</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.attendees.map((attendee) => (
                        <tr key={attendee.id} className="border-b border-border hover:bg-muted/50">
                          <td className="py-3 px-4 font-medium">{attendee.name}</td>
                          <td className="py-3 px-4 text-muted-foreground">{attendee.email}</td>
                          <td className="py-3 px-4 text-muted-foreground">{attendee.provider_number || '-'}</td>
                          <td className="py-3 px-4 text-muted-foreground">
                            {attendee.arrival_time ? format(parseISO(attendee.arrival_time), 'h:mm a') : '-'}
                          </td>
                          <td className="py-3 px-4 text-muted-foreground">
                            {attendee.departure_time ? format(parseISO(attendee.departure_time), 'h:mm a') : '-'}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                              attendee.certificate_sent 
                                ? 'bg-emerald-100 text-emerald-700'
                                : attendee.checked_in
                                ? 'bg-sky-100 text-sky-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {attendee.certificate_sent ? 'Complete' : attendee.checked_in ? 'Checked In' : 'Registered'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        ) : selectedEvent === '' ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <BarChart3 className="w-16 h-16 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Select an Event</h3>
              <p className="text-muted-foreground">
                Choose an event from the dropdown to view its report
              </p>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </Layout>
  );
}
